import React, { useEffect, useRef, useState } from 'react';
import { FiEdit2 } from 'react-icons/fi';
import './PosItemCard.css';

const formatINR = (val) => {
  try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(val || 0)); } catch { return val; }
};

export default function ItemCard({ item, qtyInCart = 0, onAdd, onEdit, pulse, onIncrement, onDecrement }) {
  const getPrimaryImage = (it) => {
    if (Array.isArray(it?.images) && it.images.length) return it.images[0];
    return it?.imageUrl || it?.image || it?.photo || it?.thumbnail || '';
  };

  const getImages = (it) => {
    const arr = Array.isArray(it?.images) ? it.images.filter(Boolean) : [];
    if (arr.length) return arr;
    const single = getPrimaryImage(it);
    return single ? [single] : [];
  };

  const getInitials = (name) => {
    const s = String(name || '').trim();
    if (!s) return 'P';
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return (parts[0].slice(0, 1) + parts[1].slice(0, 1)).toUpperCase();
  };

  const images = getImages(item);
  const [imageIndex, setImageIndex] = useState(0);
  const swipeStartXRef = useRef(null);
  const swipeDidRef = useRef(false);

  const activeIdx = Math.max(0, Math.min(Number(imageIndex || 0), Math.max(images.length - 1, 0)));
  const activeImg = images[activeIdx] || '';

  const setActiveIdx = (next) => {
    if (!images.length) return;
    const n = ((next % images.length) + images.length) % images.length;
    setImageIndex(n);
  };

  const handleCardClick = (e) => {
    // Preserve swipe gestures on the carousel (ignore click after swipe)
    if (swipeDidRef.current) return;
    const rect = e.currentTarget?.getBoundingClientRect?.();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const isLeftSide = x < rect.width / 2;
    if (isLeftSide) {
      if (qty > 0) onDecrement && onDecrement();
      return;
    }
    // Right side increments (parent handler already adds if missing)
    if (onIncrement) onIncrement();
    else if (onAdd) onAdd();
  };

  const qty = Math.max(0, Number(qtyInCart ?? 0));
  const prevQtyRef = useRef(qty);
  const flashTimerRef = useRef(null);
  const [qtyFlash, setQtyFlash] = useState(''); // 'inc' | 'dec' | ''

  useEffect(() => {
    const prev = prevQtyRef.current;
    if (qty === prev) return;
    prevQtyRef.current = qty;
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    setQtyFlash(qty > prev ? 'inc' : 'dec');
    flashTimerRef.current = window.setTimeout(() => setQtyFlash(''), 450);
    return () => {
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    };
  }, [qty]);

  const stockLabel = item?.isService ? 'Service' : `Stock: ${String(item?.inventory ?? 0)}`;
  const lowStock = !item?.isService && Number(item?.inventory ?? 0) < 10;

  const gstText = (() => {
    const cg = Number(item?.cgstRate || 0);
    const sg = Number(item?.sgstRate || 0);
    const ig = Number(item?.igstRate || 0);
    const cs = Number(item?.cessRate || 0);
    const parts = [];
    if (ig > 0) parts.push(`IGST ${ig}%`);
    else {
      if (cg > 0) parts.push(`CGST ${cg}%`);
      if (sg > 0) parts.push(`SGST ${sg}%`);
    }
    if (cs > 0) parts.push(`Cess ${cs}%`);
    return parts.length ? parts.join(' • ') : 'GST 0%';
  })();

  return (
    <div
      className={`pos-productCard ${pulse ? 'pos-productCard--pulse' : ''}`}
      onClick={handleCardClick}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit && onEdit(); }}
        title="Edit"
        className="pos-productCard__edit"
        type="button"
      >
        <FiEdit2 />
      </button>
      <div
        className="pos-productCard__carousel"
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => { swipeStartXRef.current = e.clientX; }}
        onPointerUp={(e) => {
          const start = swipeStartXRef.current;
          if (typeof start !== 'number') return;
          const dx = e.clientX - start;
          swipeStartXRef.current = null;
          if (Math.abs(dx) < 40) return;
          swipeDidRef.current = true;
          window.setTimeout(() => { swipeDidRef.current = false; }, 250);
          if (dx < 0) setActiveIdx(activeIdx + 1);
          else setActiveIdx(activeIdx - 1);
        }}
        onPointerCancel={() => { swipeStartXRef.current = null; }}
      >
        {activeImg ? (
          <img className="pos-productCard__img" src={activeImg} alt="" loading="lazy" draggable={false} />
        ) : (
          <div className="pos-productCard__imgFallback">{getInitials(item?.name)}</div>
        )}

        {images.length > 1 && (
          <>
            <button
              type="button"
              className="pos-productCard__arrow left"
              aria-label="Previous image"
              onClick={(ev) => { ev.stopPropagation(); setActiveIdx(activeIdx - 1); }}
            >
              ‹
            </button>
            <button
              type="button"
              className="pos-productCard__arrow right"
              aria-label="Next image"
              onClick={(ev) => { ev.stopPropagation(); setActiveIdx(activeIdx + 1); }}
            >
              ›
            </button>
            <div className="pos-productCard__dots" aria-hidden="true">
              {images.map((_, di) => (
                <span key={di} className={`pos-productCard__dot ${di === activeIdx ? 'active' : ''}`} />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="pos-productCard__top">
        <div>
          <div className="pos-productCard__name">{item?.name || 'Item'}</div>
          <div className="pos-productCard__priceInline">
            <span className="pos-productCard__priceValue">{formatINR(item?.price)}</span>
            <span className="pos-productCard__unit">/ {(item?.unit || 'unit').toLowerCase()}</span>
          </div>
          {!!item?.description && <div className="pos-productCard__desc">{item.description}</div>}
        </div>

        <div className="pos-productCard__metaRow">
          <span className={`pos-productCard__chip ${lowStock ? 'danger' : ''}`}>{stockLabel}</span>
          {!!item?.category && <span className="pos-productCard__chip">Category {item.category}</span>}
          {!!item?.hsnsac && <span className="pos-productCard__chip">HSN/SAC {item.hsnsac}</span>}
        </div>

        <div className="pos-productCard__footnote">GST: {gstText}</div>

        <div className="pos-productCard__controls" onClick={(e) => e.stopPropagation()}>
          <div className="pos-qty">
            <button
              type="button"
              className="pos-qty__icon"
              title="Decrease"
              aria-label="Decrease"
              disabled={qty <= 0}
              aria-disabled={qty <= 0}
              onClick={() => { if (qty <= 0) return; onDecrement && onDecrement(); }}
            >
              -
            </button>
            <div className={`pos-qty__label ${qtyFlash ? `pos-qty__label--${qtyFlash}` : ''}`}>
              {qty > 0 ? `Qty ${qty}` : 'Add'}
            </div>
            <button
              type="button"
              className="pos-qty__icon"
              title="Increase"
              aria-label="Increase"
              onClick={() => { onIncrement ? onIncrement() : (onAdd && onAdd()); }}
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


